import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle2, XCircle, Clock, MessageCircleWarning } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import apiClient from '../../api/axios';

const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;
const PHONE_REGEX = /^[6-9]\d{9}$/;

function Notifications() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phoneInput, setPhoneInput] = useState('');
  const [consentInput, setConsentInput] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const applyData = useCallback((res) => {
    setData(res);
    setPhoneInput(res.phone ? res.phone.replace('+91', '') : '');
    setConsentInput(!!res.consent);
    setEditing(res.status === 'NOT_CONFIGURED');
  }, []);

  const fetchStatus = useCallback(() => {
    return apiClient.get('/api/settings/manager-whatsapp').then((r) => {
      applyData(r.data);
      return r.data;
    });
  }, [applyData]);

  useEffect(() => {
    setLoading(true);
    fetchStatus().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while PENDING
  useEffect(() => {
    if (!data || data.status !== 'PENDING') {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      return;
    }

    setPollTimedOut(false);
    if (!pollDeadlineRef.current) {
      pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    }

    pollTimerRef.current = setInterval(() => {
      if (Date.now() > pollDeadlineRef.current) {
        clearInterval(pollTimerRef.current);
        setPollTimedOut(true);
        return;
      }
      fetchStatus().then((res) => {
        if (res.status === 'VERIFIED') {
          setToast({ type: 'success', message: t('toast.verified') });
        }
        if (res.status !== 'PENDING') {
          pollDeadlineRef.current = null;
        }
      });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(pollTimerRef.current);
  }, [data, fetchStatus, t]);

  const fullPhone = `+91${phoneInput}`;
  const phoneValid = PHONE_REGEX.test(phoneInput);
  const canVerify = phoneValid && consentInput;

  const handleSaveAndVerify = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/settings/manager-whatsapp', {
        phone: fullPhone,
        consent: consentInput,
      });
      const res = await apiClient.post('/api/settings/manager-whatsapp/verify');
      applyData(res.data);
      pollDeadlineRef.current = null;
      setToast({ type: 'success', message: t('toast.verifySent') });
    } catch (err) {
      const message = err.response?.data?.message || t('toast.verifyFailed');
      setToast({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async () => {
    setSaving(true);
    try {
      const res = await apiClient.post('/api/settings/manager-whatsapp/verify');
      applyData(res.data);
      pollDeadlineRef.current = null;
      setPollTimedOut(false);
      setToast({ type: 'success', message: t('toast.verifySent') });
    } catch (err) {
      const message = err.response?.data?.message || t('toast.verifyFailed');
      setToast({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOnly = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put('/api/settings/manager-whatsapp', {
        phone: fullPhone,
        consent: consentInput,
      });
      applyData(res.data);
      setToast({ type: 'success', message: t('toast.saved') });
    } catch (err) {
      const message = err.response?.data?.message || t('toast.saveFailed');
      setToast({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const startChangeNumber = () => {
    setEditing(true);
  };

  const status = data?.status ?? 'NOT_CONFIGURED';

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[60] flex items-center gap-2 rounded-lg border px-4 py-3 text-sm shadow-lg max-w-xs ${
            toast.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          <span className="flex-1">{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-current opacity-60 hover:opacity-100 focus:outline-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={[
          <button
            key="back"
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
          >
            <ChevronLeft size={15} />
            {t('back')}
          </button>,
        ]}
      />

      <div className="flex-1 px-6 py-6 max-w-xl mx-auto w-full">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="w-6 h-6 animate-spin text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
            </svg>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{t('card.title')}</h2>
              <p className="text-xs text-gray-500 mt-1">{t('card.description')}</p>
            </div>

            <StatusBadge status={status} t={t} />

            {editing ? (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('phoneLabel')}</label>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500">
                      +91
                    </span>
                    <input
                      type="tel"
                      value={phoneInput}
                      onChange={(e) => setPhoneInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder={t('phonePlaceholder')}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                    />
                  </div>
                  {phoneInput.length > 0 && !phoneValid && (
                    <p className="text-xs text-red-500 mt-1">{t('phoneInvalid')}</p>
                  )}
                </div>

                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentInput}
                    onChange={(e) => setConsentInput(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-300"
                  />
                  <span className="text-xs text-gray-600 leading-snug">{t('consentLabel')}</span>
                </label>

                {status !== 'NOT_CONFIGURED' && (
                  <p className="text-xs text-amber-600">{t('changeWarning')}</p>
                )}

                {status !== 'NOT_CONFIGURED' ? (
                  <button
                    onClick={handleSaveOnly}
                    disabled={saving || !phoneValid}
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 disabled:opacity-50 disabled:cursor-not-allowed transition hover:from-orange-600 hover:to-amber-700"
                  >
                    {t('buttons.save')}
                  </button>
                ) : (
                  <button
                    onClick={handleSaveAndVerify}
                    disabled={saving || !canVerify}
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 disabled:opacity-50 disabled:cursor-not-allowed transition hover:from-orange-600 hover:to-amber-700"
                  >
                    {t('buttons.verify')}
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="text-sm text-gray-900 font-medium">{data?.phone}</div>

                {status === 'PENDING' && (
                  <>
                    <button
                      onClick={handleResend}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
                    >
                      {t('buttons.resend')}
                    </button>
                    {pollTimedOut && (
                      <p className="text-xs text-amber-600">{t('status.stillWaiting')}</p>
                    )}
                  </>
                )}

                {status === 'VERIFIED' && (
                  <button
                    onClick={startChangeNumber}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
                  >
                    {t('buttons.changeNumber')}
                  </button>
                )}

                {status === 'DECLINED' && (
                  <button
                    onClick={handleResend}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 disabled:opacity-50 transition hover:from-orange-600 hover:to-amber-700"
                  >
                    {t('buttons.tryAgain')}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const config = {
    NOT_CONFIGURED: { icon: XCircle, text: t('status.notConfigured'), cls: 'text-gray-500 bg-gray-50 border-gray-200' },
    PENDING: { icon: Clock, text: t('status.pending'), cls: 'text-blue-700 bg-blue-50 border-blue-200 animate-pulse' },
    VERIFIED: { icon: CheckCircle2, text: t('status.verified'), cls: 'text-green-700 bg-green-50 border-green-200' },
    DECLINED: { icon: MessageCircleWarning, text: t('status.declined'), cls: 'text-red-700 bg-red-50 border-red-200' },
  }[status];

  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${config.cls}`}>
      <Icon size={15} />
      {config.text}
    </div>
  );
}

export default Notifications;
